/* ============================================================
   Plentum dog quiz — the "Get my dog's plan" popup.
   Spec: "Plentum Dog Quiz — Intake + Recommendation Engine" v2.0 (2026-09-25).
   9 questions (name, breed, age, size + 5 wellness areas) → a routine of Advanced K9
   formulas. Microbiome Care is always the foundation; a wellness score ≥ 2 adds that
   area's booster; score 1 lands in "you might also consider"; some intake answers add
   auto-flags with a one-line reason. Bundle size picks the named tier.
   Open it with any [data-dog-quiz-open] or a[href="#dog-quiz"]; the dialog markup and
   live product data come from snippets/dog-quiz.liquid. The engine below has no DOM
   dependency — tests/dog-quiz.test.js runs it under node.
   ============================================================ */
(function (root) {
  'use strict';

  /* ---------- questions (copy from spec §3–§4; score = null means "none of the above") ---------- */
  var NONE = 'None of the above / Not applicable';
  var QUESTIONS = {
    breed: {
      title: 'What breed is {dog}?',
      options: [
        { label: 'Purebred — small companion', hint: 'Yorkie, Pomeranian, Maltese, Chihuahua…', short: 'purebred' },
        { label: 'Purebred — medium', hint: 'Beagle, Cocker Spaniel, Bulldog…', short: 'purebred' },
        { label: 'Purebred — large', hint: 'Labrador, Golden Retriever, German Shepherd…', short: 'purebred' },
        { label: 'Purebred — giant', hint: 'Great Dane, Mastiff, Saint Bernard…', short: 'purebred' },
        { label: 'Purebred — flat-faced', hint: 'Bulldog, Pug, Frenchie, Boxer…', short: 'flat-faced breed' },
        { label: "Mixed breed / I don't know / None of the above", short: '' }
      ]
    },
    age: {
      title: 'How old is {dog}?',
      options: [
        { label: 'Under 1 year', hint: 'Puppy', short: 'puppy' },
        { label: '1–3 years', hint: 'Young adult', short: 'young adult' },
        { label: '4–7 years', hint: 'Adult', short: 'adult' },
        { label: '8+ years', hint: 'Senior', short: 'senior' },
        { label: 'Not sure / None of the above', none: true, short: '' }
      ]
    },
    size: {
      title: 'What size is {dog}?',
      sub: 'This sets the pack size we suggest.',
      options: [
        { label: 'Under 20 lb', hint: 'Toy or small', short: 'small', weight: 14 },
        { label: '20 to 25 lb', hint: 'Small', short: 'small', weight: 22 },
        { label: '26 to 50 lb', hint: 'Medium', short: 'medium', weight: 38 },
        { label: '51 to 75 lb', hint: 'Large', short: 'large', weight: 63 },
        { label: 'Over 75 lb', hint: 'Giant', short: 'giant', weight: 90 },
        { label: 'Not sure / None of the above', none: true, short: '', weight: 38 }
      ]
    },
    q5: {
      title: "How is {dogs} daily wellness?", sub: 'Energy, appetite and everyday vitality.', product: 'foundation',
      options: [
        { label: 'Excellent', hint: 'Energy, appetite, and routine all on point', score: 0 },
        { label: 'Good overall', hint: 'Small things occasionally, generally thriving', score: 1 },
        { label: 'Could use more support', hint: 'General vitality feels mid, hard to pinpoint', score: 2 },
        { label: 'Needs attention', hint: 'Noticeable dip in energy, appetite, or routine', score: 3 },
        { label: 'A lot on my mind', hint: 'Several things feel off', score: 4 },
        { label: NONE, none: true, score: null }
      ]
    },
    q6: {
      title: 'Does {dog} show signs of stress?', sub: 'Everyday calm, and how new situations land.', product: 'calming',
      options: [
        { label: 'Calm and confident', hint: 'Handles new situations easily', score: 0 },
        { label: 'Mostly settled', hint: 'Minor reactions occasionally', score: 1 },
        { label: 'Sometimes stressed', hint: 'Fireworks, vet, travel trigger visible signs', score: 2 },
        { label: 'Often stressed', hint: 'Frequent pacing, panting, hiding, or whining', score: 3 },
        { label: 'Significantly stressed', hint: 'Daily disruption from stress', score: 4 },
        { label: NONE, none: true, score: null }
      ]
    },
    q7: {
      title: "How is {dogs} skin and coat?", sub: 'Skin comfort, coat condition and the skin barrier.', product: 'itch',
      options: [
        { label: 'Healthy', hint: 'Soft coat, no scratching, no hot spots', score: 0 },
        { label: 'Generally good', hint: 'Occasional scratching', score: 1 },
        { label: 'Sometimes itchy or dull', hint: 'Visible flakes or redness sometimes', score: 2 },
        { label: 'Often uncomfortable', hint: 'Scratching, licking paws, ear issues', score: 3 },
        { label: 'Very uncomfortable', hint: 'Hot spots or hair loss. Your vet should take a look.', score: 4 },
        { label: NONE, none: true, score: null }
      ]
    },
    q8: {
      title: "How is {dogs} digestion?", sub: 'Stool quality, regularity and food tolerance.', product: 'gut',
      options: [
        { label: 'Perfect', hint: 'Regular, comfortable stools, no issues', score: 0 },
        { label: 'Mostly good', hint: 'Occasional loose stool or sensitivity', score: 1 },
        { label: 'Sometimes off', hint: 'Sensitivity to foods, inconsistent stools', score: 2 },
        { label: 'Often upset', hint: 'Frequent tummy issues, gas, loose stools', score: 3 },
        { label: 'Hard most days', hint: 'Frequent tummy trouble or gas. Worth a chat with your vet.', score: 4 },
        { label: NONE, none: true, score: null }
      ]
    },
    q9: {
      title: 'Is {dog} at a healthy weight?', sub: 'Metabolism, appetite and body condition.', product: 'weight',
      options: [
        { label: 'At ideal weight', hint: 'Vet confirms, easy to maintain', score: 0 },
        { label: 'A little extra', hint: 'A few pounds', score: 2 },
        { label: 'Quite a bit extra', hint: 'Slower on walks', score: 3 },
        { label: 'Well over', hint: 'Your vet has mentioned it', score: 4 },
        { label: 'Significantly under', hint: 'Struggling to maintain weight', score: 3 },
        { label: NONE, none: true, score: null }
      ]
    }
  };

  /* ---------- engine (spec §5–§7) ---------- */
  var BOOSTERS = { calming: 'q6', itch: 'q7', gut: 'q8', weight: 'q9' };
  /* §6.3 keep-priority: gut, then itch; calming vs weight is decided by the higher score (tie → calming) */
  var GROUP = { gut: 1, itch: 2, calming: 3, weight: 3 };
  var TIE = { gut: 0, itch: 0, calming: 0, weight: 1 };
  var TIERS = [
    { key: 'foundation', name: 'Daily Foundation', desc: 'The everyday sachet that supports gut, oral, and immune health.' },
    { key: 'duo', name: 'Daily Duo', desc: 'Foundation plus one targeted booster.' },
    { key: 'trio', name: 'Daily Trio', desc: 'Foundation plus two targeted boosters.' },
    { key: 'stack', name: 'Daily Four', desc: 'Foundation plus three targeted boosters.' },
    { key: 'system', name: 'The whole range', desc: 'All five supplements, working together.' }
  ];

  function scoreOf(q, idx) {
    var o = QUESTIONS[q].options[idx];
    return o ? o.score : null;
  }

  /* answers: option indexes (0-based) for breed, age, size, q5…q9.
     opts.maxBoosters caps the bundle (default 4 = no cap); trimmed boosters move to "consider". */
  function recommend(answers, opts) {
    var max = opts && opts.maxBoosters != null ? opts.maxBoosters : 4;
    var s = {
      foundation: scoreOf('q5', answers.q5),
      calming: scoreOf('q6', answers.q6),
      itch: scoreOf('q7', answers.q7),
      gut: scoreOf('q8', answers.q8),
      weight: scoreOf('q9', answers.q9)
    };
    var strong = Object.keys(BOOSTERS).filter(function (k) { return s[k] != null && s[k] >= 2; });
    strong.sort(function (a, b) { return GROUP[a] - GROUP[b] || s[b] - s[a] || TIE[a] - TIE[b]; });
    var bundle = ['foundation'].concat(strong.slice(0, max));

    /* §7 auto-flags: shown on the product's card if it's in the routine, otherwise in "consider" */
    var flags = {};
    var flag = function (k, why) { (flags[k] = flags[k] || []).push(why); };
    if (answers.age === 0) flag('foundation', 'Under 1 year: ask your vet before starting a supplement.');
    if (answers.age === 3) {
      flag('foundation', 'Supports vitality and immune-skin health in seniors.');
      flag('itch', 'Supports vitality and immune-skin health in seniors.');
    }
    if (answers.breed === 4) flag('itch', 'Daily skin and coat support for a flat-faced breed.');
    if (s.gut === 4 && s.itch != null && s.itch >= 2) flag('foundation', 'Gut-skin axis — the inside connects to the outside.');

    if (bundle.indexOf('foundation') !== -1 && bundle.indexOf('gut') !== -1) {
      flag('gut', "Start Gut Restore first with half your dog's daily dose for 3 days, then the full dose for their weight. Wait a week at the full dose before introducing Microbiome Care the same way.");
    }

    var consider = ['gut', 'itch', 'calming', 'weight'].filter(function (k) {
      return bundle.indexOf(k) === -1 && (s[k] === 1 || flags[k] || strong.indexOf(k) !== -1);
    });
    return { tier: TIERS[bundle.length - 1], bundle: bundle, consider: consider, flags: flags, scores: s };
  }

  /* variant titles like "Under 20lbs", "20-50lbs", "51–75 lbs", "Above 75 lbs" → [min, max] lb */
  function rangeOf(title) {
    var t = String(title || '').toLowerCase().replace(/,/g, '');
    var m = t.match(/(\d+(?:\.\d+)?)\s*(?:-|–|—|to)\s*(\d+(?:\.\d+)?)/);
    if (m) return [+m[1], +m[2]];
    m = t.match(/(?:under|below|up to|less than|<)\s*(\d+(?:\.\d+)?)/);
    if (m) return [0, +m[1]];
    m = t.match(/(?:above|over|more than|>)\s*(\d+(?:\.\d+)?)/) || t.match(/(\d+(?:\.\d+)?)\s*\+/);
    if (m) return [+m[1], Infinity];
    return null;
  }

  /* the dog's weight picks the variant whose range contains it; titles without weights fall
     back to the size's position in the list (smallest → largest) */
  function pickVariant(variants, sizeIdx) {
    var list = variants || []; // Keep the label-matching size even when sold out.
    if (!list.length) return null;
    var size = QUESTIONS.size.options[sizeIdx] || QUESTIONS.size.options[5];
    for (var i = 0; i < list.length; i++) {
      var r = rangeOf(list[i].title);
      if (r && size.weight >= r[0] && size.weight <= r[1]) return list[i];
    }
    var rank = [0, 0, 0.5, 1, 1, 0.5][sizeIdx == null ? 5 : sizeIdx];
    return list[Math.round(rank * (list.length - 1))];
  }

  var Engine = { QUESTIONS: QUESTIONS, TIERS: TIERS, recommend: recommend, rangeOf: rangeOf, pickVariant: pickVariant };
  if (typeof module !== 'undefined' && module.exports) { module.exports = Engine; return; }
  if (root.PlentumQuiz) return;
  root.PlentumQuiz = Engine;
  if (typeof document === 'undefined') return;

  /* ============================== UI ============================== */
  var STEPS = ['welcome', 'name', 'breed', 'age', 'size', 'transition', 'q5', 'q6', 'q7', 'q8', 'q9', 'loading', 'result'];
  var NUMBER = { name: 1, breed: 2, age: 3, size: 4, q5: 5, q6: 6, q7: 7, q8: 8, q9: 9 };
  var NAME_OK = /^[\p{L}][\p{L} \-]{0,29}$/u;
  var FALLBACK_TITLE = {
    foundation: 'Advanced K9 Microbiome Care', calming: 'Advanced K9 Calming Care',
    itch: 'Advanced K9 Itch & Immune Care', gut: 'Advanced K9 Gut Restore', weight: 'Advanced K9 Metabolic Care'
  };

  var dlg, body, data, lastFocus, loadingTimer;
  var state = { step: 'welcome', a: {}, mode: 'once', added: {} };

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function dogName() { return state.a.name || ''; }
  function list(names) { return names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]; }
  function fill(text) {
    var n = dogName();
    return text.replace(/\{dogs\}/g, n ? n + "'s" : "your dog's").replace(/\{dog\}/g, n || 'your dog');
  }
  function track(name, detail) {
    try {
      var d = Object.assign({ event: name }, detail || {});
      (window.dataLayer = window.dataLayer || []).push(d);
      document.dispatchEvent(new CustomEvent('plentum:quiz', { detail: d }));
    } catch (e) { /* analytics must never break the quiz */ }
  }
  function money(cents) {
    var fmt = (data && data.moneyFormat) || '${{amount}}';
    var n = (cents || 0) / 100;
    var parts = function (dec, thou, sep) {
      var fixed = n.toFixed(dec).split('.');
      return fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (dec ? sep + fixed[1] : '');
    };
    return String(fmt).replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
      if (k === 'amount_no_decimals') return parts(0, ',', '.');
      if (k === 'amount_with_comma_separator') return parts(2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return parts(0, '.', ',');
      return parts(2, ',', '.');
    });
  }

  /* ---------- product helpers ---------- */
  function product(k) {
    var p = (data.products && data.products[k]) || {};
    var v = pickVariant(p.variants, state.a.size);
    var sub = state.mode === 'sub' && v && v.plan;
    return {
      key: k,
      title: p.title || FALLBACK_TITLE[k],
      benefit: p.benefit || '',
      image: p.image,
      url: p.url && v ? p.url + (p.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + v.id + '&band=' + (['u20', '21-25', '26-50', '51-75', 'o75'][state.a.size] || '26-50') : p.url,
      variant: v,
      buyable: !!(v && v.available),
      price: v ? (sub ? v.plan.price : v.price) : null,
      was: v && sub && v.plan.price < v.price ? v.price : null,
      plan: sub ? v.plan.id : null
    };
  }
  function anyPlan(keys) {
    return keys.some(function (k) { var p = product(k); return p.variant && p.variant.plan; });
  }

  /* ---------- open / close ---------- */
  function open() {
    if (!dlg) return;
    lastFocus = document.activeElement;
    if (!dlg.open) {
      if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
    }
    document.documentElement.classList.add('dq-lock');
    if (state.step === 'welcome') track('quizStart');
    render();
  }
  function close() {
    if (!dlg) return;
    clearTimeout(loadingTimer);
    if (state.step === 'loading') state.step = 'q9';
    if (typeof dlg.close === 'function' && dlg.open) dlg.close(); else dlg.removeAttribute('open');
    document.documentElement.classList.remove('dq-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function go(step) { state.step = step; render(); }
  function back() {
    var i = STEPS.indexOf(state.step);
    if (i > 0) go(STEPS[i - 1] === 'loading' ? 'q9' : STEPS[i - 1]);
  }
  function next() {
    var i = STEPS.indexOf(state.step);
    var to = STEPS[i + 1];
    if (to === 'loading') {
      state.result = recommend(state.a, { maxBoosters: 2 }); /* foundation + up to 2 boosters: 5 formulas for a 60 lb dog would be 15 sachets a day */
      state.added = {};
      track('quizCompleted', { quizTier: state.result.tier.name, quizBundle: state.result.bundle.join(',') });
      go('loading');
      loadingTimer = setTimeout(function () {
        track('quizRecommendedBundle', { quizTier: state.result.tier.name, quizBundle: state.result.bundle.join(',') });
        go('result');
      }, 1300);
      return;
    }
    go(to);
  }

  /* ---------- screens ---------- */
  function screenWelcome() {
    return '<div class="dq__screen dq__screen--center">' +
      '<p class="dq__eyebrow">60-second assessment</p>' +
      '<h2 class="dq__title" tabindex="-1">Let\'s build your dog\'s daily routine.</h2>' +
      '<p class="dq__lede">Nine quick questions, then we\'ll suggest where to start for your dog.</p>' +
      '<button type="button" class="dq__cta" data-dq-go="name">Start</button></div>';
  }
  function screenName() {
    var n = dogName();
    return '<form class="dq__screen" data-dq-name novalidate>' +
      '<h2 class="dq__title" tabindex="-1" id="dq-q">What\'s your dog\'s name?</h2>' +
      '<label class="dq__field"><span class="dq__sr">Your dog\'s name</span>' +
      '<input type="text" name="dog" maxlength="30" autocomplete="off" autocapitalize="words" spellcheck="false" value="' + esc(n) + '" aria-describedby="dq-name-note dq-name-err"></label>' +
      '<p class="dq__note" id="dq-name-note">We\'ll use this to build <span data-dq-live>' + esc(n ? n + "'s" : "your dog's") + '</span> daily routine.</p>' +
      '<p class="dq__error" id="dq-name-err" role="alert" hidden>Just letters, spaces or hyphens, up to 30, please.</p>' +
      '<button type="submit" class="dq__cta">Continue</button></form>';
  }
  function screenChoice(q) {
    var Qd = QUESTIONS[q];
    var picked = state.a[q];
    var html = '<div class="dq__screen">' +
      '<h2 class="dq__title" tabindex="-1" id="dq-q">' + esc(fill(Qd.title)) + '</h2>' +
      (Qd.sub ? '<p class="dq__sub">' + esc(Qd.sub) + '</p>' : '') +
      '<div class="dq__opts" role="group" aria-labelledby="dq-q">';
    Qd.options.forEach(function (o, i) {
      html += '<button type="button" class="dq__opt' + (o.none ? ' dq__opt--none' : '') + '" data-dq-pick="' + i + '" aria-pressed="' + (picked === i) + '">' +
        '<span class="dq__opt-label">' + esc(o.label) + '</span>' +
        (o.hint ? '<span class="dq__opt-hint">' + esc(o.hint) + '</span>' : '') + '</button>';
    });
    return html + '</div></div>';
  }
  function screenTransition() {
    return '<div class="dq__screen dq__screen--center">' +
      '<p class="dq__eyebrow">Great — ' + esc(dogName()) + ' is all set up</p>' +
      '<h2 class="dq__title" tabindex="-1">Now, how\'s ' + esc(fill('{dog}')) + ' doing day to day?</h2>' +
      '<p class="dq__lede">Daily energy, stress, skin and coat, digestion and weight. Pick what you see on most days.</p>' +
      '<button type="button" class="dq__cta" data-dq-go="q5">Continue</button></div>';
  }
  function screenLoading() {
    return '<div class="dq__screen dq__screen--center"><span class="dq__spinner" aria-hidden="true"></span>' +
      '<h2 class="dq__title" tabindex="-1">Building ' + esc(fill('{dogs}')) + ' daily routine…</h2></div>';
  }
  function poTag(url) {
    var h = ((url || '').split('/products/')[1] || '').split(/[?#/]/)[0];
    return window.PlentumPreorder && h ? window.PlentumPreorder.tag(h) : '';
  }
  function card(k, extra) {
    var p = product(k);
    var reasons = (state.result.flags[k] || []).filter(function (r, i, all) { return all.indexOf(r) === i; });
    var img = p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy" width="64" height="64">' : '<span aria-hidden="true">' + esc(p.title.replace('Advanced K9 ', '').charAt(0)) + '</span>';
    var price = !p.buyable
      ? '<span class="dq__soon">Coming soon</span>'
      : (p.was ? '<span class="dq__price-line"><strong>' + money(p.price) + '</strong> with Subscribe &amp; Save</span><span class="dq__price-once">' + money(p.was) + ' one-time</span>' : money(p.price));
    return '<li class="dq__card">' +
      '<div class="dq__card-img">' + img + '</div>' +
      '<div class="dq__card-main">' +
        '<p class="dq__card-name">' + (p.url ? '<a href="' + esc(p.url) + '">' + esc(p.title) + '</a>' : esc(p.title)) + '</p>' +
        (poTag(p.url) ? '<p class="po-res">' + poTag(p.url) + '</p>' : '') +
        (p.benefit ? '<p class="dq__card-why">' + esc(p.benefit) + '</p>' : '') +
        reasons.map(function (r) { return '<p class="dq__card-flag">' + esc(r) + '</p>'; }).join('') +
        (p.buyable && p.variant && p.variant.title && p.variant.title !== 'Default Title' ? '<p class="dq__card-meta">Size: ' + esc(p.variant.title) + '</p>' : '') +
      '</div>' +
      '<div class="dq__card-side"><span class="dq__card-price">' + price + '</span>' + (extra || '') + '</div></li>';
  }
  function addButton(k) {
    var p = product(k);
    if (!p.buyable) return '';
    var done = state.added[k];
    return '<button type="button" class="dq__add" data-dq-add="' + k + '"' + (done ? ' disabled' : '') + '>' + (done ? 'Added ✓' : '+ Add') + '</button>';
  }
  function screenResult() {
    var r = state.result;
    var a = state.a;
    var words = [QUESTIONS.age.options[a.age], QUESTIONS.size.options[a.size]]
      .map(function (o) { return o && o.short; }).filter(Boolean);
    words.push((QUESTIONS.breed.options[a.breed] || {}).short || 'dog');
    var phrase = words.join(' ');
    var lede = 'Based on your answers for ' + (/^[aeiou]/i.test(phrase) ? 'an ' : 'a ') + phrase + '.';
    var items = r.bundle.map(product);
    var buyable = items.filter(function (p) { return p.buyable; });
    var missing = items.filter(function (p) { return !p.buyable; });
    var total = buyable.reduce(function (sum, p) { return sum + p.price; }, 0);
    var showModes = anyPlan(r.bundle);
    var rest = ['gut', 'itch', 'calming', 'weight'].filter(function (k) { return r.bundle.indexOf(k) === -1 && r.consider.indexOf(k) === -1; });

    var html = '<div class="dq__screen dq__screen--result">' +
      '<p class="dq__eyebrow">Here\'s where we\'d start</p>' +
      '<h2 class="dq__title" tabindex="-1">' + esc(fill('{dogs}')) + ' daily routine</h2>' +
      '<p class="dq__lede">' + esc(lede) + '</p>' +
      (a.size === 5 ? '<p class="dq__hint">Not sure of the size? We\'ve started with the medium pack — weigh ' + esc(fill('{dog}')) + ' and adjust it in the cart.</p>' : '') +
      '<div class="dq__bundle">' +
        '<p class="dq__tier-label">Recommended</p>' +
        '<p class="dq__tier">' + esc(r.tier.name) + '</p>' +
        '<p class="dq__tier-desc">' + esc(r.tier.desc) + '</p>' +
        '<ul class="dq__cards">' + r.bundle.map(function (k) { return card(k); }).join('') + '</ul>';
    if (showModes) {
      html += '<div class="dq__modes" role="group" aria-label="Purchase option">' +
        '<button type="button" class="dq__mode" data-dq-mode="sub" aria-pressed="' + (state.mode === 'sub') + '">Subscribe &amp; save</button>' +
        '<button type="button" class="dq__mode" data-dq-mode="once" aria-pressed="' + (state.mode === 'once') + '">One-time</button></div>';
    }
    if (buyable.length) {
      html += '<p class="dq__total"><span>' + (state.mode === 'sub' && showModes ? 'Per delivery' : 'Total') + '</span><strong>' + money(total) + '</strong></p>';
    }
    html += '<button type="button" class="dq__cta dq__cta--wide" data-dq-add="bundle"' + (buyable.length ? '' : ' disabled') + '>' +
        (r.bundle.length > 1 ? 'Add routine to cart' : 'Add to cart') + '</button>' +
        (missing.length && buyable.length ? '<p class="dq__small">' + esc(list(missing.map(function (p) { return p.title.replace('Advanced K9 ', ''); }))) + (missing.length > 1 ? " aren't" : " isn't") + ' available to order yet — we\'ll add the rest.</p>' : '') +
        '<p class="dq__msg" data-dq-msg role="status"></p>' +
        (data.trust ? '<p class="dq__trust">' + esc(data.trust) + '</p>' : '') +
      '</div>';
    if (r.consider.length) {
      html += '<p class="dq__section-title">Worth a look too</p><ul class="dq__cards">' +
        r.consider.map(function (k) { return card(k, addButton(k)); }).join('') + '</ul>';
    }
    if (rest.length) {
      html += '<details class="dq__all"><summary class="dq__link">See all 5 supplements</summary><ul class="dq__cards">' +
        rest.map(function (k) { return card(k, addButton(k)); }).join('') + '</ul></details>';
    }
    /* T-AUDIT17 U-4/U-5: vet-first guard chips (always; the age chip is lit when the quiz answer was under 1 year) and the 12-week module */
    var young = a.age === 0;
    html += '<div class="xg' + (young ? ' is-alert' : '') + '" role="note"><ul class="xg__row"><li class="xg__chip' + (young ? ' is-on' : '') + '">Under 1 year</li><li class="xg__chip">On medication</li><li class="xg__chip">Pregnant or nursing</li><li class="xg__go">\u2192 Ask your vet first</li></ul>' +
      '<p class="xg__t">' + (young ? esc(fill('{dog}')) + ' is under 1 year. Check with your vet before starting any supplement.' : 'If any of these apply to your dog, check with your vet before starting any supplement.') + '</p></div>';
    var xt = document.querySelector('[data-dq-expect]'); if (xt) html += xt.innerHTML;
    return html + '<button type="button" class="dq__link dq__retake" data-dq-retake>Take the quiz again for another dog →</button></div>';
  }

  function render() {
    var step = state.step;
    var n = NUMBER[step];
    var head = dlg.querySelector('[data-dq-progress]');
    head.hidden = !(n >= 2);
    if (n) {
      dlg.querySelector('[data-dq-bar]').style.width = Math.round(n / 9 * 100) + '%';
      dlg.querySelector('[data-dq-count]').textContent = n + ' of 9';
    }
    dlg.querySelector('[data-dq-back]').style.visibility = (step === 'welcome' || step === 'loading') ? 'hidden' : 'visible';
    var html = step === 'welcome' ? screenWelcome()
      : step === 'name' ? screenName()
      : step === 'transition' ? screenTransition()
      : step === 'loading' ? screenLoading()
      : step === 'result' ? screenResult()
      : screenChoice(step);
    body.innerHTML = html;
    body.scrollTop = 0;
    var focus = step === 'name' ? body.querySelector('input') : body.querySelector('.dq__title');
    if (focus) focus.focus({ preventScroll: true });
  }

  /* ---------- actions ---------- */
  function onBodyClick(e) {
    var t = e.target.closest('button, [data-dq-go]');
    if (!t || !body.contains(t)) return;
    if (t.hasAttribute('data-dq-go')) { go(t.getAttribute('data-dq-go')); return; }
    if (t.hasAttribute('data-dq-pick')) {
      var q = state.step;
      var i = +t.getAttribute('data-dq-pick');
      state.a[q] = i;
      Array.prototype.forEach.call(body.querySelectorAll('[data-dq-pick]'), function (b) { b.setAttribute('aria-pressed', b === t); });
      track('quizQuestionAnswered', { quizQuestion: q, quizAnswer: QUESTIONS[q].options[i].label });
      setTimeout(function () { if (state.step === q) next(); }, 220);
      return;
    }
    if (t.hasAttribute('data-dq-mode')) {
      state.mode = t.getAttribute('data-dq-mode');
      var y = body.scrollTop;
      render();
      body.scrollTop = y;
      return;
    }
    if (t.hasAttribute('data-dq-add')) { addToCart(t.getAttribute('data-dq-add'), t); return; }
    if (t.hasAttribute('data-dq-retake')) {
      state.a = {};
      state.result = null;
      track('quizStart', { quizRetake: true });
      go('name');
    }
  }
  function onSubmit(e) {
    e.preventDefault();
    var input = body.querySelector('input[name="dog"]');
    var v = input.value.trim().replace(/\s+/g, ' ');
    var ok = NAME_OK.test(v);
    body.querySelector('.dq__error').hidden = ok;
    input.setAttribute('aria-invalid', !ok);
    if (!ok) { input.focus(); return; }
    state.a.name = v;
    track('quizQuestionAnswered', { quizQuestion: 'name' });
    next();
  }
  function onInput(e) {
    if (e.target.name !== 'dog') return;
    var live = body.querySelector('[data-dq-live]');
    var v = e.target.value.trim();
    if (live) live.textContent = v ? v + "'s" : "your dog's";
  }

  function addToCart(which, btn) {
    var keys = which === 'bundle' ? state.result.bundle : [which];
    var items = [];
    keys.forEach(function (k) {
      var p = product(k);
      if (!p.buyable) return;
      var it = { id: p.variant.id, quantity: 1 };
      if (p.plan) it.selling_plan = p.plan;
      items.push(it);
    });
    if (!items.length) return;
    var msg = body.querySelector('[data-dq-msg]');
    var drawer = document.querySelector('cart-drawer');
    var payload = { items: items };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      payload.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    btn.disabled = true;
    btn.classList.add('dq--busy');
    var url = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) throw new Error(res.description || res.message || 'Could not add to cart');
        track('quizAddedToCart', { quizTier: state.result.tier.name, quizItems: keys.join(','), quizAddType: which === 'bundle' ? 'bundle' : 'single' });
        /* bundle-core's publish/PUB_SUB_EVENTS are script-level globals, not window properties */
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dog-quiz', cartData: res }); } catch (err) { /* optional */ }
        }
        if (which === 'bundle') {
          close();
          if (drawer && res.sections && typeof drawer.renderContents === 'function') {
            drawer.classList.remove('is-empty');
            drawer.renderContents(res);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        } else {
          state.added[which] = true;
          if (drawer && res.sections && typeof drawer.renderContents === 'function') {
            drawer.classList.remove('is-empty');
            drawer.renderContents(res, false); /* refresh cart + count, keep the quiz open */
          }
          btn.textContent = 'Added ✓';
        }
      })
      .catch(function (err) {
        btn.disabled = false;
        if (msg) { msg.textContent = err.message; msg.classList.add('dq__msg--err'); }
      })
      .finally(function () { btn.classList.remove('dq--busy'); });
  }

  /* ---------- boot ---------- */
  function init() {
    if (dlg) return;
    dlg = document.querySelector('[data-dq]');
    if (!dlg) return;
    body = dlg.querySelector('[data-dq-body]');
    try { data = JSON.parse(document.getElementById('dog-quiz-data').textContent); } catch (e) { data = { products: {} }; }
    dlg.querySelector('[data-dq-close]').addEventListener('click', close);
    dlg.querySelector('[data-dq-back]').addEventListener('click', back);
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); }); /* Esc */
    dlg.addEventListener('click', function (e) { if (e.target === dlg) close(); }); /* backdrop */
    body.addEventListener('click', onBodyClick);
    body.addEventListener('submit', onSubmit);
    body.addEventListener('input', onInput);
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-dog-quiz-open], a[href="#dog-quiz"]');
    if (!t) return;
    e.preventDefault();
    init();
    open();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', function () { dlg = null; init(); });
})(typeof window !== 'undefined' ? window : this);


